import React, { useState, useMemo, ChangeEvent, useCallback } from 'react';
import SearchIcon from '@atlaskit/icon/glyph/search';
import classnames from 'classnames';
import Textfield from '@atlaskit/textfield';
import Button from '@atlaskit/button';
import { token } from '@atlaskit/tokens';
import DropdownMenu, { DropdownItemCheckbox, DropdownItemGroupCheckbox } from '@atlaskit/dropdown-menu';
import SelectClearIcon from '@atlaskit/icon/glyph/select-clear';
import uniq from 'lodash/uniq';
import debounce from 'lodash/debounce';
import isEmpty from 'lodash/isEmpty';
import { useCartProducts, useStoreActions } from '../../../store/store';
import { CartProductSchema, ProductStatus } from '../../../types';
import './cart-filter-wrapper.scss';

export const CartFilterWrapper = ({ enableDesignIteration1Changes }: { enableDesignIteration1Changes?: boolean }) => {
  const cartProducts = useCartProducts();
  const actions = useStoreActions();
  const [searchQuery, setSearchQuery] = useState('');
  const [statuses, toggleStatuses] = useState<Record<ProductStatus, boolean>>({
    [ProductStatus.NEW]: false,
    [ProductStatus.EDITED]: false,
    [ProductStatus.TRANSFER]: false,
    [ProductStatus.CANCEL]: false,
  });

  const syncWithStore = useCallback(
    ({ searchQuery, statuses }) => {
      const selectedStatuses = Object.keys(statuses).filter(
        (status) => statuses[status as ProductStatus]
      ) as ProductStatus[];

      actions.setSearchParams({
        searchQuery,
        statuses: selectedStatuses,
      });
    },
    [actions]
  );

  const debouncedSyncWithStore = useMemo(() => debounce(syncWithStore, 500), [syncWithStore]);

  const selectedProductStatuses = useMemo(() => {
    let allStatuses: ProductStatus[] = [];
    cartProducts.forEach(({ productStatuses }) => {
      if (!productStatuses) return;

      allStatuses = allStatuses.concat(productStatuses);
    });

    return uniq(allStatuses);
  }, [cartProducts]);

  const clearSearchBtn = (
    <Button
      appearance="subtle"
      iconAfter={<SelectClearIcon label="" size="small" />}
      onClick={() => {
        setSearchQuery('');
        actions.clearSearchQuery();
      }}
      isDisabled={isEmpty(searchQuery)}
    />
  );

  return (
    <div
      className={classnames('flex align-center ppc-cart-filter-wrapper', {
        'mt-16 pa-16': !enableDesignIteration1Changes, //these classnames are not required for version 2
        'mb-16': enableDesignIteration1Changes, //these classnames are not required for version 2
      })}
    >
      <div className="ppc-cart-search-box">
        <Textfield
          elemBeforeInput={
            <SearchIcon
              label=""
              size={enableDesignIteration1Changes ? 'medium' : 'small'}
              primaryColor={token('color.text.subtlest')}
            />
          }
          elemAfterInput={clearSearchBtn}
          className="ppc-cart-filter-wrapper-search-field"
          onChange={(e: ChangeEvent<HTMLInputElement>) => {
            setSearchQuery(e.target.value);
            debouncedSyncWithStore({
              searchQuery: e.target.value,
              statuses,
            });
          }}
          value={searchQuery}
          placeholder="Search"
        />
      </div>
      {selectedProductStatuses.length > 0 && (
        <DropdownMenu
          trigger="Filters"
          triggerType="button"
          triggerButtonProps={{
            className: 'filter-button',
            isDisabled: selectedProductStatuses.length === 0,
          }}
        >
          <DropdownItemGroupCheckbox id="actions">
            {selectedProductStatuses.map((status) => (
              <DropdownItemCheckbox
                key={status}
                id={status}
                onClick={() => {
                  const updatedStatuses = {
                    ...statuses,
                    [status]: !statuses[status],
                  };

                  toggleStatuses(updatedStatuses);
                  debouncedSyncWithStore({
                    searchQuery,
                    statuses: updatedStatuses,
                  });
                }}
                isSelected={statuses[status]}
              >
                {status}
              </DropdownItemCheckbox>
            ))}
          </DropdownItemGroupCheckbox>
        </DropdownMenu>
      )}
    </div>
  );
};
